import { useState, useEffect } from 'react';
import { Download, ParkingCircle, Users, Car, TrendingUp, ChevronDown } from 'lucide-react';
import '../styles/ReportesStyle.css'
import * as XLSX from 'xlsx';
import lprService from '../services/lprService';
import parkingService from '../services/parkingService';
import apiClient from '../services/apiClient';

const Reportes = () => {
  const [fechaPlaca, setFechaPlaca] = useState('');
  const [plateFilter, setPlateFilter] = useState('');

  const [error, setError] = useState(null);

  const [placasData, setPlacasData] = useState([]);
  const [placasLoading, setPlacasLoading] = useState(false);
  const [paginacion, setPaginacion] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 50
  });

  const [filtros, setFiltros] = useState({
    camera: '',
    startDate: '',
    endDate: ''
  });

  // Estados para la sección de parqueos
  const [parkings, setParkings] = useState([]);
  const [selectedParkingId, setSelectedParkingId] = useState('');
  const [parkingInfo, setParkingInfo] = useState(null);
  const [parkingLoading, setParkingLoading] = useState(false);
  const [entradasHoy, setEntradasHoy] = useState(0);
  const [totalUsuarios, setTotalUsuarios] = useState(0);

  // Cargar datos al montar el componente
  useEffect(() => {
    cargarHistorialPlacas();
    cargarParqueos();
    cargarTotalUsuarios();
  }, [paginacion.currentPage, filtros]);

  const cargarParqueos = async () => {
    try {
      const data = await parkingService.getAllParkings();
      setParkings(data);
    } catch (err) {
      console.error('Error cargando parqueos:', err);
    }
  };

  const cargarTotalUsuarios = async () => {
    try {
      const response = await apiClient.get('/userApp/getAllUserApp');
      const data = response.data;
      setTotalUsuarios(Array.isArray(data) ? data.length : 0);
    } catch (err) {
      console.error('Error cargando total de usuarios:', err);
    }
  };

  const handleParkingSelect = async (parkingId) => {
    setSelectedParkingId(parkingId);
    if (!parkingId) {
      setParkingInfo(null);
      setEntradasHoy(0);
      return;
    }

    setParkingLoading(true);

    try {
      // Obtener datos FRESCOS del parqueo (con currentOccupancy actualizado por LPR)
      const freshParking = await parkingService.getParkingById(parkingId);
      const parkingData = freshParking.parking || freshParking;
      setParkingInfo(parkingData);

      // Obtener entradas del día desde LPR history
      const hoy = new Date();
      const inicioDelDia = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()).toISOString();
      const finDelDia = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate(), 23, 59, 59).toISOString();

      const response = await lprService.getLicensePlateHistory({
        startDate: inicioDelDia,
        endDate: finDelDia,
        parkingName: parkingData.parkingName,
      });

      setEntradasHoy(response.total || response.readings?.length || 0);
    } catch (err) {
      console.error('Error cargando datos del parqueo:', err);
      // Fallback: usar datos cacheados de la lista
      const parking = parkings.find(p => p._id === parkingId);
      setParkingInfo(parking);
      setEntradasHoy(0);
    } finally {
      setParkingLoading(false);
    }
  };

  const filtrarPlacas = () => {
    const newFiltros = { camera: '', startDate: '', endDate: '', plate: '' };

    if (fechaPlaca) {
      const fecha = new Date(fechaPlaca + 'T00:00:00');
      newFiltros.startDate = fecha.toISOString();
      const fin = new Date(fechaPlaca + 'T23:59:59');
      newFiltros.endDate = fin.toISOString();
    }

    if (plateFilter) {
      newFiltros.plate = plateFilter.trim().toUpperCase();
    }

    setFiltros(newFiltros);
    setPaginacion(prev => ({ ...prev, currentPage: 1 }));
  };


  const cargarHistorialPlacas = async () => {
    try {
      setPlacasLoading(true);
      setError('');

      const params = {
        page: paginacion.currentPage,
        limit: paginacion.limit,
        ...(filtros.camera && { camera: filtros.camera }),
        ...(filtros.startDate && { startDate: filtros.startDate }),
        ...(filtros.endDate && { endDate: filtros.endDate }),
        ...(filtros.plate && { plate: filtros.plate }),
      };

      const response = await lprService.getLicensePlateHistory(params);

      // Mapear los datos para la tabla
      const placasMapeadas = response.readings.map(reading => ({
        id: reading._id,
        dia: formatDate(reading.timestamp),
        placa: reading.plateNumber,
        imagen: reading.imagePath || 'N/A',
        formato: reading.plateFormat || 'N/A',
        pais: reading.country || 'N/A',
        direccion: reading.direction || 'N/A',
        confianza: reading.confidence,
        camara: reading.cameraId,
        // Datos adicionales del vehículo si están disponibles
        vehiculo: reading.vehicleInfo ? {
          marca: reading.vehicleInfo.make,
          modelo: reading.vehicleInfo.model,
          año: reading.vehicleInfo.year,
          propietario: reading.vehicleInfo.owner
        } : null
      }));

      setPlacasData(placasMapeadas);
      setPaginacion(prev => ({
        ...prev,
        totalPages: response.pages,
        total: response.total
      }));

    } catch (error) {
      console.error('Error cargando historial de placas:', error);
      setError('Error al cargar el historial de placas');
    } finally {
      setPlacasLoading(false);
    }
  };


  const exportarAExcel = (datos_tabla) => {
    try {
      // Crear una hoja de cálculo
      const worksheet = XLSX.utils.json_to_sheet(datos_tabla);

      // Crear un libro de trabajo
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Datos");

      // Generar el archivo Excel y descargarlo
      XLSX.writeFile(workbook, "datos_tabla.xlsx");
      console.log("Archivo Excel generado correctamente");
    } catch (error) {
      console.error("Error al generar el archivo Excel:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <div className="rpt-container">
      <h1 className="rpt-main-title">Reportes</h1>

      {error && (
        <div className="rpt-error-message">
          {error}
        </div>
      )}

      <div className="rpt-tables-grid">
        {/* Tabla de Placas Registradas */}
        <div className="rpt-sidebar-card">
          <h2 className="rpt-card-title">Placas registradas</h2>
          <div className="rpt-table-wrapper">
            <table className="rpt-data-table">
              <thead className="rpt-table-header">
                <tr>
                  <th className="rpt-header-cell">Día</th>
                  <th className="rpt-header-cell">Placa</th>
                  <th className="rpt-header-cell">Imagen</th>
                  <th className="rpt-header-cell">Camara</th>
                  <th className="rpt-header-cell">Confianza</th>

                </tr>
              </thead>
              <tbody>
                {placasData.map((plate, index) => (
                  <tr key={plate.id} className={index % 2 === 0 ? 'rpt-row-even' : 'rpt-row-odd'}>
                    <td className="rpt-data-cell">{plate.dia}</td>
                    <td className="rpt-data-cell rpt-plate-cell">
                      <span className="rpt-plate-badge">{plate.placa}</span>
                    </td>
                    <td className="rpt-data-cell rpt-data-cell-muted">{plate.imagen ? 'Sí' : 'No'}</td>
                    <td className="rpt-data-cell">{plate.camara}</td>
                    <td className="rpt-data-cell rpt-data-cell-muted">{plate.confianza}</td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rpt-controls">
            <input
              type="date"
              className="rpt-date-input"
              placeholder="mm/dd/yyyy"
              value={fechaPlaca}
              onChange={(e) => setFechaPlaca(e.target.value)}
            />
            <input
              type="text"
              className="rpt-filter-input"
              placeholder="Número de placa"
              value={plateFilter}
              onChange={(e) => setPlateFilter(e.target.value)}
            />
            <button className="rpt-filter-btn" onClick={filtrarPlacas}>
              Filtrar
            </button>
            <button className="rpt-download-btn" onClick={() => exportarAExcel(placasData)}>
              <Download size={16} />
            </button>
          </div>
        </div>

        {/* Sección de información de parqueos */}
        <div className="rpt-sidebar-card">
          <h2 className="rpt-card-title">Información de parqueos</h2>

          {/* Dropdown de parqueos */}
          <div className="rpt-parking-select-wrapper">
            <ParkingCircle size={18} className="rpt-parking-select-icon" />
            <select
              className="rpt-parking-select"
              value={selectedParkingId}
              onChange={(e) => handleParkingSelect(e.target.value)}
            >
              <option value="">Seleccionar parqueo...</option>
              {parkings.map((p) => (
                <option key={p._id} value={p._id}>{p.parkingName}</option>
              ))}
            </select>
            <ChevronDown size={16} className="rpt-parking-select-chevron" />
          </div>

          {/* Panel de info del parqueo seleccionado */}
          {parkingLoading && (
            <div className="rpt-loading-message">Cargando datos del parqueo...</div>
          )}

          {parkingInfo && !parkingLoading && (
            <div className="rpt-parking-info">
              <div className="rpt-parking-info-header">
                <h3 className="rpt-parking-info-name">{parkingInfo.parkingName}</h3>
                <span className="rpt-parking-info-tariff">₡{parkingInfo.tarify || 0}/hr</span>
              </div>

              <div className="rpt-parking-stats-grid">
                {/* Ocupación actual */}
                <div className="rpt-stat-card rpt-stat-ocupacion">
                  <div className="rpt-stat-icon-wrapper rpt-stat-icon-blue">
                    <Car size={20} />
                  </div>
                  <div className="rpt-stat-content">
                    <span className="rpt-stat-value">
                      {parkingInfo.currentOccupancy || 0} / {parkingInfo.capacity || 0}
                    </span>
                    <span className="rpt-stat-label">Ocupación actual</span>
                    <div className="rpt-stat-bar">
                      <div
                        className="rpt-stat-bar-fill"
                        style={{
                          width: `${parkingInfo.capacity ? Math.min((parkingInfo.currentOccupancy / parkingInfo.capacity) * 100, 100) : 0}%`,
                          backgroundColor: parkingInfo.capacity && (parkingInfo.currentOccupancy / parkingInfo.capacity) > 0.85 ? '#ef4444' :
                            (parkingInfo.currentOccupancy / parkingInfo.capacity) > 0.6 ? '#f59e0b' : '#22c55e'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Entradas del día */}
                <div className="rpt-stat-card rpt-stat-entradas">
                  <div className="rpt-stat-icon-wrapper rpt-stat-icon-green">
                    <TrendingUp size={20} />
                  </div>
                  <div className="rpt-stat-content">
                    <span className="rpt-stat-value">{entradasHoy}</span>
                    <span className="rpt-stat-label">Placas leídas hoy</span>
                  </div>
                </div>

                {/* Usuarios registrados (total global) */}
                <div className="rpt-stat-card rpt-stat-usuarios">
                  <div className="rpt-stat-icon-wrapper rpt-stat-icon-purple">
                    <Users size={20} />
                  </div>
                  <div className="rpt-stat-content">
                    <span className="rpt-stat-value">{totalUsuarios}</span>
                    <span className="rpt-stat-label">Usuarios registrados</span>
                  </div>
                </div>
              </div>

              {/* Info adicional */}
              <div className="rpt-parking-extra">
                <div className="rpt-parking-extra-item">
                  <span className="rpt-parking-extra-label">Espacios disponibles</span>
                  <span className="rpt-parking-extra-value">
                    {Math.max((parkingInfo.capacity || 0) - (parkingInfo.currentOccupancy || 0), 0)}
                  </span>
                </div>
                <div className="rpt-parking-extra-item">
                  <span className="rpt-parking-extra-label">Capacidad total</span>
                  <span className="rpt-parking-extra-value">{parkingInfo.capacity || 0}</span>
                </div>
                <div className="rpt-parking-extra-item">
                  <span className="rpt-parking-extra-label">Porcentaje ocupado</span>
                  <span className="rpt-parking-extra-value">
                    {parkingInfo.capacity ? ((parkingInfo.currentOccupancy / parkingInfo.capacity) * 100).toFixed(1) : 0}%
                  </span>
                </div>
              </div>
            </div>
          )}

          {!selectedParkingId && !parkingLoading && (
            <div className="rpt-parking-empty">
              <ParkingCircle size={48} strokeWidth={1} />
              <p>Selecciona un parqueo para ver su información</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reportes;